/**
 * metricTreeTemplates — starter trees for the "Not sure where to start?" chooser.
 * These are EMPTY scaffolds (Mixpanel parity): placeholder nodes with no metric
 * bound. The user connects a metric to each card (or writes up each Bet). Nothing
 * is pre-filled and nothing is AI-generated.
 *
 * Returns backend-shape { name, default_time_frame, nodes, edges } that the canvas
 * maps through toRfNode / toRfEdge.
 */

// ── node builders ──
const metricNode = (id, label, x, y) => ({
  node_id: id, type: 'metric', kind: 'metric', label,
  metric: { agg: 'count' }, position: { x, y },
});
const betNode = (id, label, x, y) => ({
  node_id: id, type: 'strategy', label,
  annotation: { text: 'Write about your data…' }, position: { x, y },
});
const textNode = (id, label, description, x, y) => ({
  node_id: id, type: 'text', label, annotation: { description }, position: { x, y },
});
// hierarchy connector: child connects UP to parent (top → bottom), arrow at parent.
const hedge = (id, child, parent) => ({
  edge_id: id, source: child, target: parent,
  source_handle: 't', target_handle: 'b', kind: 'correlation',
});

// KPI Tree — top-down: Level 1 (Root KPI) → Level 2 (3) → Level 3 (9) → Bets (9),
// with a labelled band down the left. Exact structure of Mixpanel's KPI template.
export function buildKpiTree() {
  // MT-14: first metric column starts at x=480 (was 360) so the x=20 lane-label
  // band (~240 wide) keeps a clear gutter and the first node never covers it.
  const L3X = (i) => 480 + i * 250; // 9 columns across
  const ROOT_Y = 40;
  const L2_Y = 320;
  const L3_Y = 580;
  const BET_Y = 840;

  const nodes = [
    textNode('t_l1', 'Level 1 (Strategic KPIs)', 'Broad, top-level metric capturing overall direction or success.', 20, ROOT_Y),
    textNode('t_l2', 'Level 2 (Tactical KPIs)', 'Mid-level metrics connecting strategy to functions or domains.', 20, L2_Y),
    textNode('t_l3', 'Level 3 (Operational KPIs)', 'Specific, low-level metrics tracking detailed activities or processes.', 20, L3_Y),
    textNode('t_bets', 'Bets', 'Strategic initiatives or experiments to improve operational metrics.', 20, BET_Y),
    metricNode('root', 'Root KPI', 1360, ROOT_Y),
    metricNode('l2_1', 'Level 2 - Input Metric 1', 610, L2_Y),
    metricNode('l2_2', 'Level 2 - Input Metric 2', 1360, L2_Y),
    metricNode('l2_3', 'Level 2 - Input Metric 3', 2110, L2_Y),
  ];
  for (let i = 0; i < 9; i += 1) nodes.push(metricNode(`l3_${i + 1}`, `Level 3 - Input Metric ${i + 1}`, L3X(i), L3_Y));
  for (let i = 0; i < 9; i += 1) nodes.push(betNode(`bet_${i + 1}`, `Bet ${i + 1}`, L3X(i), BET_Y));

  const edges = [
    hedge('e_r1', 'l2_1', 'root'), hedge('e_r2', 'l2_2', 'root'), hedge('e_r3', 'l2_3', 'root'),
  ];
  const l2Parent = ['l2_1', 'l2_1', 'l2_1', 'l2_2', 'l2_2', 'l2_2', 'l2_3', 'l2_3', 'l2_3'];
  for (let i = 0; i < 9; i += 1) edges.push(hedge(`e_l3_${i + 1}`, `l3_${i + 1}`, l2Parent[i]));
  for (let i = 0; i < 9; i += 1) edges.push(hedge(`e_bet_${i + 1}`, `bet_${i + 1}`, `l3_${i + 1}`));

  return { name: 'KPI Tree', default_time_frame: 'all_time', nodes, edges };
}

// North Star Framework — left-to-right causal chain: Bets → Input metrics → the
// North Star Metric → Output metrics, in four labelled columns. Exact structure of
// Mixpanel's North Star template. Empty scaffold.
export function buildNorthStar() {
  const BETS_X = 40;
  const INPUT_X = 360;
  const NS_X = 700;
  const OUT_X = 1040;
  // MT-14: metric rows start at y=210 (was 130) so the label band at y=20 (which
  // shares each column's x) clears the first row instead of overlapping it.
  const rowY = [210, 410, 610, 810];
  const outY = [310, 510, 710];

  const nodes = [
    textNode('t_bets', 'Bets', 'Strategic initiatives or experiments to improve input metrics.', BETS_X, 20),
    textNode('t_input', 'Input', 'Metrics that represent the controllable levers teams can pull to drive growth.', INPUT_X, 20),
    textNode('t_ns', 'North Star', 'A metric to rally the company around, a leading indicator for business KPIs.', NS_X, 20),
    textNode('t_out', 'Outputs', 'Metrics that reflect the outcomes of user behavior and business performance.', OUT_X, 20),
    metricNode('ns_star', 'North Star Metric', NS_X, 510),
  ];
  for (let i = 0; i < 4; i += 1) nodes.push(betNode(`nbet_${i + 1}`, `Bet ${i + 1}`, BETS_X, rowY[i]));
  for (let i = 0; i < 4; i += 1) nodes.push(metricNode(`in_${i + 1}`, `Input Metric ${i + 1}`, INPUT_X, rowY[i]));
  for (let j = 0; j < 3; j += 1) nodes.push(metricNode(`out_${j + 1}`, `Output Metric ${j + 1}`, OUT_X, outY[j]));

  const edges = [];
  for (let i = 0; i < 4; i += 1) edges.push({ edge_id: `ns_be_${i + 1}`, source: `nbet_${i + 1}`, target: `in_${i + 1}`, kind: 'correlation' });
  for (let i = 0; i < 4; i += 1) edges.push({ edge_id: `ns_ie_${i + 1}`, source: `in_${i + 1}`, target: 'ns_star', kind: 'correlation' });
  for (let j = 0; j < 3; j += 1) edges.push({ edge_id: `ns_oe_${j + 1}`, source: 'ns_star', target: `out_${j + 1}`, kind: 'correlation' });

  return { name: 'North Star Framework', default_time_frame: 'all_time', nodes, edges };
}

// Chooser card metadata. `layout` tells the canvas how to arrange on seed.
export const TEMPLATES = [
  {
    key: 'kpi',
    title: 'KPI Tree',
    blurb: 'Maps metrics to goals — show how KPIs drive business objectives.',
    accent: '#F5D9A0',
    layout: 'fixed',
    orientation: 'vertical', // top-down
    build: buildKpiTree,
  },
  {
    key: 'northstar',
    title: 'North Star Framework',
    blurb: 'Aligns teams to one key metric, a leading indicator for outputs.',
    accent: '#9BE0D6',
    layout: 'fixed',
    orientation: 'horizontal', // left-to-right
    build: buildNorthStar,
  },
];

export const templateByKey = (key) => TEMPLATES.find((t) => t.key === key);
